import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd'
import {
  EditFilenameModalState,
  UseEditFilenameModal,
} from '../hooks/useEditFilenameModal/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import {
  CustomFieldDataType,
  CustomListItemStyle,
  FilenameTemplateItem,
  Report,
} from '@cims-online/shared'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'

export function CompatibleTemplateItems<T extends EditFilenameModalState>({
  state,
  setTemplateItems,
  isDisabled,
}: UseEditFilenameModal<T>) {
  const { customFields } = useProjectCustomFieldsContext()
  const [open, { add, remove }] = useKeyedBooleans()
  const confirm = (id: FilenameTemplateItem['id']) => {
    remove(id)
    setTemplateItems((prev) =>
      Array.of(...prev, { type: 'templateItem', value: id })
    )
  }
  return (
    <Space wrap>
      <Tooltip key="custom" title="Add a text box for custom text">
        <Button
          onClick={() =>
            setTemplateItems((prev) =>
              Array.of(...prev, { type: 'custom', value: '' })
            )
          }
        >
          Custom Text
        </Button>
      </Tooltip>
      {(state?.compatibleTemplateItems ?? []).map((item) => {
        const disabled = isDisabled(item)
        const { id, name, help, property } = item
        return (
          <Tooltip
            key={id}
            title={disabled ? `Cannot select ${name} if unset` : help}
          >
            <Popconfirm
              title={
                <Space direction="vertical" size="small">
                  <Typography.Text>
                    {name} uses a list of items.
                  </Typography.Text>
                  <Typography.Text>
                    To help mitigate maximum filename length constraints, the
                    first 5 characters of each item will be used.
                  </Typography.Text>
                </Space>
              }
              open={open(id)}
              onOpenChange={(nextOpen) => {
                if (!nextOpen) return remove(id)
                if (property === 'tags' || property === 'tagCategories') {
                  add(id)
                } else confirm(id)
              }}
              onConfirm={() => confirm(id)}
              onCancel={() => remove(id)}
              placement="bottom"
            >
              <Button disabled={disabled}>{name}</Button>
            </Popconfirm>
          </Tooltip>
        )
      })}
      {state?.id === Report.ConstructionAdminSubmittal.id &&
        customFields
          .filter(
            ({ dataTypeId, metadata }) =>
              dataTypeId === CustomFieldDataType.Number.id ||
              dataTypeId === CustomFieldDataType.Date.id ||
              (dataTypeId === CustomFieldDataType.List.id &&
                metadata.styleId === CustomListItemStyle.Single.id)
          )
          .map(({ name, description, id }) => (
            <Tooltip key={id} title={description}>
              <Button
                onClick={() =>
                  setTemplateItems((prev) =>
                    Array.of(...prev, { type: 'customField', value: id })
                  )
                }
              >
                {name}
              </Button>
            </Tooltip>
          ))}
    </Space>
  )
}

export default CompatibleTemplateItems
